import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const SUBMIT_URL = 'https://readdy.ai/api/form/da22n0rk8r5te5mqserg';

const contactChannels = [
  { icon: 'ri-mail-line', title: '官方邮箱', desc: 'support@sakuramanga.example.com', href: 'mailto:support@sakuramanga.example.com' },
  { icon: 'ri-weibo-line', title: '新浪微博', desc: '@樱花漫画网官方', href: '#' },
  { icon: 'ri-bilibili-fill', title: '哔哩哔哩', desc: '樱花漫画网动画号', href: '#' },
];

export default function ContactPage() {
  useDocumentMeta(
    '联系我们 - 樱花漫画网',
    '如有内容建议、版权合作或使用反馈，欢迎通过联系页面与樱花漫画网沟通。我们会在工作日及时回复，一起把阅读体验做得更好。',
  );

  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get('contact_alt') as string) || '';
    if (honeypot.trim()) {
      setStatus('success');
      return;
    }

    const email = (fd.get('email') as string) || '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('请输入有效的邮箱地址。');
      return;
    }

    fd.delete('contact_alt');

    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试。');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        {/* 横幅 */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex items-center gap-1.5">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li>/</li>
                <li aria-current="page" className="text-foreground-800">联系我们</li>
              </ol>
            </nav>
            <h1 className="font-heading mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h1>
            <p className="mt-2 text-sm text-foreground-600">无论内容建议、版权合作还是使用反馈，我们都期待听到你的声音。</p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* 表单 */}
            <div className="lg:col-span-2">
              <div className="rounded-xl bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950">给我们留言</h2>
                <p className="mt-1 text-sm text-foreground-500">我们通常会在 1-2 个工作日内回复。</p>

                {status === 'success' ? (
                  <div className="mt-6 rounded-lg bg-primary-50 px-5 py-6 text-center text-primary-800">
                    <i className="ri-checkbox-circle-fill text-3xl text-primary-500"></i>
                    <p className="mt-3 font-semibold">留言已收到，感谢你的反馈！</p>
                    <p className="mt-1 text-sm">我们会尽快处理你的问题。</p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼</label>
                        <input
                          id="cf-name"
                          type="text"
                          name="name"
                          required
                          placeholder="怎么称呼你"
                          className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
                        />
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱地址</label>
                        <input
                          id="cf-email"
                          type="email"
                          name="email"
                          required
                          placeholder="用于回复你"
                          className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="cf-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">留言主题</label>
                      <select
                        id="cf-subject"
                        name="subject"
                        className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400"
                      >
                        <option>内容建议</option>
                        <option>版权合作</option>
                        <option>阅读反馈</option>
                        <option>其他问题</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="cf-message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
                      <textarea
                        id="cf-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="请描述你的问题或建议（500 字以内）"
                        className="w-full resize-none rounded-lg bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
                      ></textarea>
                      <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                    </div>
                    <div className="flex flex-col items-center gap-3 pt-2 sm:flex-row">
                      <button
                        type="submit"
                        className="w-full rounded-full bg-primary-500 px-8 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition hover:bg-primary-600 sm:w-auto"
                      >
                        提交留言
                      </button>
                      {status === 'error' && (
                        <p className="text-sm text-accent-700">{formError}</p>
                      )}
                    </div>
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field"
                    />
                  </form>
                )}
              </div>
            </div>

            {/* 联系渠道 */}
            <aside aria-label="联系渠道">
              <h2 className="font-heading text-xl font-bold text-foreground-950">其他联系方式</h2>
              <div className="mt-5 space-y-3">
                {contactChannels.map((c) => (
                  <a
                    key={c.title}
                    href={c.href}
                    className="flex items-center gap-4 rounded-xl bg-background-100 p-4 transition hover:bg-primary-100"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className={`${c.icon} text-xl`}></i>
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-foreground-900">{c.title}</span>
                      <span className="block text-xs text-foreground-500">{c.desc}</span>
                    </span>
                  </a>
                ))}
              </div>
              <div className="mt-5 rounded-xl bg-background-100 p-5 text-sm leading-6 text-foreground-600">
                <p className="font-semibold text-foreground-900">工作时间</p>
                <p className="mt-1">周一至周五 09:00 - 18:00</p>
                <p className="mt-2 text-xs text-foreground-500">周末与节假日的留言，我们会在下一个工作日统一处理。</p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />

      {/* ContactPage 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: '联系我们 - 樱花漫画网',
          inLanguage: 'zh-CN',
          mainEntity: {
            '@type': 'Organization',
            name: '樱花漫画网',
            email: 'support@sakuramanga.example.com',
          },
        })}
      </script>
    </>
  );
}